import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FiUploadCloud } from "react-icons/fi";
import { Input } from "../../../../../components/ui/input";
import { z } from "zod";
import { Button } from "../../../../../components/ui/button";
import { Textarea } from "../../../../../components/ui-comp/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { updateTarget } from "../../../../../helpers/mutations/targets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../../components/ui/form";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../../components/ui/radio-group";
import { Label } from "../../../../../components/ui/label";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Trash2 } from "lucide-react";
import textImg from "../../../../../assets/images/text-img.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import TagsInput from "../../../../../components/TagsInput";

const EditTargetModal = ({ data, openEdit, setOpenEdit, assetList }) => {
  const [type, setType] = useState("manual");

  const getFormSchema = (type) =>
    z
      .object({
        name: z.string().min(1, "Name cannot be empty"),
        asset_type: z.string().min(1, "Please select a type"),
        target: z.string().optional(),
        assetListFile: z
          .union([z.instanceof(File), z.string().nullable(), z.null()])
          .optional(),
        description: z.string().optional(),
      })
      .refine(
        (data) => {
          if (type === "manual") {
            return !!data.target?.trim();
          } else {
            return data.assetListFile instanceof File;
          }
        },
        {
          message: "This field is required",
          path: [type === "manual" ? "target" : "assetListFile"],
        }
      );
  const formSchema = getFormSchema(type);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: data?.name,
      target: assetList ? data?.target?.data : data.target,
      asset_type: data?.asset_type,
      description: data?.description,
    },
  });

  const { control, register, setValue, watch } = form;

  useEffect(() => {
    if (data) {
      form.reset({
        name: data.name || "",
        target: assetList ? data.target : data?.target?.data || "",
        asset_type: data.asset_type || "",
        description: data.description || "",
      });
    }
  }, [data, assetList, form]);

  const file = watch("assetListFile");
  const assetType = watch("asset_type");

  const removeFile = () => {
    setValue("assetListFile", null);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "text/plain": [".txt"] },
    multiple: false,
    onDrop: (files) => {
      if (files.length > 0) {
        // onFileSelect(files[0]);
        setValue("assetListFile", files[0], { shouldValidate: true });
      }
    },
  });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, data }) => updateTarget(id, data),
    onSuccess: (data) => {
      toast(data?.message);
      queryClient.invalidateQueries({
        queryKey: ["targets"],
      });
      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
      form.reset();
      setOpenEdit(false);
    },
    onError: (data) => {
      toast.error(data.message);
    },
  });

  const onSubmit = (formData) => {
    const payload = {
      name: formData.name,
      asset_type: formData.asset_type,
      description: formData.description,
      ...(type === "manual"
        ? { target: formData.target }
        : { assetListFile: formData.assetListFile }),
    };
    mutate({ id: data?.id, data: payload });
  };

  return (
    <Dialog open={openEdit} onOpenChange={setOpenEdit}>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Edit Asset
          </DialogTitle>
          <DialogDescription>
            Modify the details of the website or IP address you'd like to
            monitor
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-1"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="asset_type"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Asset Type</FormLabel>
                  <Select
                    onValueChange={
                      field.onChange // Update the form field value
                    }
                    value={field?.value}
                    defaultValue={data?.asset_type}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select asset type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="web">Web Asset</SelectItem>
                      <SelectItem value="infrastructure">
                        Infrastructure Asset
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col">
              <label
                htmlFor="inputType"
                className="text-sm font-medium text-neutral1000 mb-3"
              >
                Host Input Type
              </label>
              <RadioGroup
                // defaultValue="manual"
                value={type}
                className="flex items-center gap-12 mb-2"
                onValueChange={(value) => setType(value)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="manual"
                    id="r1"
                    disabled={!assetType}
                  />
                  <Label htmlFor="r1" className="m-0">
                    Manual Entry
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="upload"
                    id="r2"
                    disabled={!assetType}
                  />
                  <Label htmlFor="r2" className="m-0">
                    File Upload
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {type === "manual" && (
              <>
                <FormField
                  control={form.control}
                  name="target"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain(s) or IP Address(es)</FormLabel>
                      {/* <Input
                        {...field}
                        placeholder={
                          assetType === "web"
                            ? "https://example.com, http://example.com, https://example.com/login.php"
                            : "E.g 192.168.8.1,192.168.8.2 or target.com"
                        }
                        onChange={() => console.log(field.value)} // save as string
                      /> */}
                      <TagsInput
                        // value={field.value || []}
                        // onChange={field.onChange}

                        // value={field.value ? field.value.split(",") : []} // show as array
                        // onChange={(tags) => field.onChange(tags.join(","))} // save as string

                        value={
                          Array.isArray(field.value)
                            ? field.value
                            : typeof field.value === "string" &&
                              field.value.length > 0
                            ? field.value.split(",")
                            : []
                        }
                        onChange={(tags) => field.onChange(tags.join(","))} // always save as string
                        placeholder={
                          assetType === "web"
                            ? "https://example.com, http://example.com, https://example.com/login.php"
                            : "E.g 192.168.8.1, 192.168.8.2, target.com"
                        }
                      />
                      <FormMessage /> {/* Will now show the message */}
                    </FormItem>
                  )}
                />
              </>
            )}

            {type === "upload" && (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload a .txt file containing your IPs/domains.{" "}
                    <span className="text-red-500">*</span>
                  </label>

                  <div className="border border-[#3636361A] rounded-lg p-6">
                    {!file ? (
                      <FormField
                        control={control}
                        name="assetListFile"
                        render={() => (
                          <>
                            <FormItem>
                              <div
                                {...getRootProps()}
                                className="flex flex-col items-center justify-center  cursor-pointer text-center bg-white hover:border-gray-400 transition-colors"
                              >
                                <input
                                  {...getInputProps()}
                                  // onChange={(e) => {
                                  //   onChange(e.target.files?.[0]); // correctly set the file object
                                  // }}
                                  // {...rest}
                                />

                                <FiUploadCloud className="text-2xl mb-2 text-[#B7B7B9]" />

                                <p className="text-sm text-neutral900 font-medium mt-3">
                                  Choose a file or drag & drop it here
                                </p>
                                <p className="text-xs text-neutral800 mt-1 mb-4">
                                  Accepted file format: txt
                                </p>

                                <p className="text-neutral900 px-4 py-2 text-sm font-medium border rounded-lg ">
                                  Browse file
                                </p>
                              </div>
                            </FormItem>
                            <FormMessage />
                          </>
                        )}
                      />
                    ) : (
                      <div className="flex items-center justify-between bg-white">
                        <div className="flex items-center gap-3">
                          <img
                            src={textImg}
                            alt="textImg"
                            className="w-[32px]"
                          />
                          <div>
                            <p className="font-medium text-sm text-gray-800">
                              {file.name}
                            </p>
                          </div>
                        </div>

                        <Trash2
                          onClick={removeFile}
                          className="text-gray-500 hover:text-red-600 transition"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col">
              <label
                htmlFor="description"
                className="text-sm  font-medium text-neutral1000"
              >
                Description
              </label>
              <Textarea
                {...register("description")}
                placeholder="Add asset description"
              ></Textarea>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white"
                loading={isPending}
              >
                Edit asset
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTargetModal;
